<template>
  <div class="companies">
    <SectionHeader>
      <template v-slot:breadcrumbs>
        <BreadCrumbs
          :items="[{ text: $t('meta.containers'), name: 'company_containers' }, { text: $t('meta.import') }]"
        />
      </template>
    </SectionHeader>

    <div class="body-content cf">
      <CompanyNav />
      <div class="body">
        <div class="content-wrap">
          <header class="content-header cf">
            <h3>{{ $t('meta.import') }} (.csv)</h3>
          </header>
          <div class="content-body">
            <LoadingSpinner v-if="importing">
              <template #content>
                <div class="alert alert--centered alert-info">
                  <p>{{ $t('meta.importing') }}</p>
                  <p>{{ importedCount }}/{{ importObjects }} {{ $t('meta.container') }} {{ $t('meta.imported') }}.</p>
                </div>
              </template>
            </LoadingSpinner>
            <div v-else>
              <vue-csv-import
                v-model="csv"
                url="/import_placeholder"
                headers="true"
                :autoMatchFields="true"
                :map-fields="mapFields"
                :finally="importNow"
                ref="csvUploader"
              >
                <template slot="error">
                  <p class="alert alert-danger">{{ $t('imports.wrongFileType') }}</p>
                </template>

                <template slot="thead">
                  <tr>
                    <th>{{ $t('imports.fields') }}</th>
                    <th>{{ $t('imports.columns') }}</th>
                  </tr>
                </template>

                <template slot="next" slot-scope="{ load }">
                  <button @click.prevent="load" class="button button-green button-round button-small">
                    {{ $t('imports.loadCsv') }}
                  </button>
                </template>

                <template slot="submit" slot-scope="{ submit }">
                  <p class="required-notice"><span>*</span> {{ $t('imports.requiredField') }}</p>
                  <button @click.prevent="submit" class="button button-green button-round button-small">
                    {{ $t('imports.importNow') }}
                  </button>
                </template>
              </vue-csv-import>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { VueCsvImport } from 'vue-csv-import'

import BreadCrumbs from '@/components/BreadCrumbs'
import LoadingSpinner from '@/components/LoadingSpinner'
import SectionHeader from '@/components/SectionHeader'
import CompanyNav from '@/components/company_nav'
import stringUtils from '@/components/mixins/stringUtils'

import containersApi from '@/api/container'
import { mapSettings } from '@/utils/settings'

export default {
  name: 'import_container',
  inject: ['companyUser'],
  data() {
    return {
      container_types: [],
      projects: [],
      csv: null,
      importing: false,
      importObjects: 0,
      importedCount: 0,
    }
  },
  components: {
    BreadCrumbs,
    CompanyNav,
    LoadingSpinner,
    VueCsvImport,
    SectionHeader,
  },
  mixins: [stringUtils],
  computed: {
    ...mapSettings(['userLanguage']),
    mapFields() {
      return containersApi.csvFieldsMap[this.userLanguage]
    },
  },
  methods: {
    importNow() {
      if (this.csv.length === 0) {
        this.$notify({
          group: 'alert',
          type: 'error',
          text: this.$t('imports.noContents'),
        })
      } else {
        const firstRow = this.csv[0]
        if (firstRow.name && firstRow.lat && firstRow.lng && firstRow.project_uuid) {
          this.importing = true
          this.importObjects = this.csv.length
          this.csv.forEach(row => {
            if (row.uuid) {
              this.updateContainer(row)
            } else {
              this.createContainer(row)
            }
          })
        } else {
          this.$notify({
            group: 'alert',
            type: 'error',
            text: this.$t('imports.requiredFieldsMissing'),
          })
        }
      }
    },
    parseNumberValues(row) {
      const numbers = ['lat', 'lng', 'type_volume']
      numbers.forEach(number => {
        if (row[number]) {
          row[number] = this.parseNumberString(row[number])
        }
      })
      return row
    },
    async createContainer(row) {
      const parsedRow = this.parseNumberValues(row)
      const params = { company_uuid: this.companyUser.company_uuid, ...parsedRow }
      const response = await containersApi.create(params)
      if (response.success) {
        this.importedCount += 1
        if (this.importObjects === this.importedCount) {
          await this.$router.push(`/company/containers`)
        }
      } else {
        this.importing = false
      }
    },
    async updateContainer(row) {
      const parsedRow = this.parseNumberValues(row)
      const updated = await containersApi.update(parsedRow.uuid, parsedRow)
      if (updated) {
        this.importedCount += 1
        if (this.importObjects === this.importedCount) {
          await this.$router.push(`/company/containers`)
        }
      } else {
        this.importing = false
      }
    },
    markRequiredFields() {
      const targetNode = this.$refs.csvUploader.$el
      const config = { childList: true, subtree: true }
      const callback = mutationList => {
        for (const mutation of mutationList) {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            const tableRows = Array.from(targetNode.querySelectorAll('.vue-csv-mapping .table tbody tr'))
            tableRows.forEach(row => {
              const field = row.children[0]
              if (containersApi.requiredFields[this.userLanguage].includes(field.innerHTML)) {
                field.classList.add('required')
              }
            })
          }
        }
      }
      const observer = new MutationObserver(callback)
      observer.observe(targetNode, config)
    },
  },
  mounted() {
    this.markRequiredFields()
  },
}
</script>

<style lang="scss">
.vue-csv-uploader {
  .form-control-file {
    min-width: 50%;
  }
  p.alert-danger,
  p.required-notice {
    margin-top: 0;
  }
  p.required-notice {
    color: var(--binity-red);
  }
  .table {
    margin: 2rem 0;
    th {
      text-transform: uppercase;
    }
    td.required::after {
      content: '*';
      color: var(--binity-red);
    }
  }
}
</style>
